import { readRobotAlerts, subscribeRobotAlerts, type RobotAlertItem } from '@/services/api/robot-alerts-store'
import { subscribeSosAlerts, type SosCovers, type SosQueueState } from '@/services/api/sos-alert-queue'
import { selectMissedSosAlerts } from '@/services/api/sos-missed'
import { useEffect, useMemo, useState } from 'react'

/** How often the list re-ages its alerts. A ringing card leaves on its own exact timer. */
const MISSED_TICK_MS = 15_000

/**
 * The open critical alarms nobody answered in time, newest first.
 *
 * `covers` narrows it to the devices a dashboard shows (the corner pill); without it the list is
 * console-wide (the bell drawer). Re-renders when the store changes, when the ringing queue moves an
 * emergency off the card, and on a slow tick so an alarm ages into the list on any page.
 */
export function useMissedEmergencies(covers?: SosCovers): RobotAlertItem[] {
  const [alerts, setAlerts] = useState<RobotAlertItem[]>(readRobotAlerts)
  const [queue, setQueue] = useState<SosQueueState | null>(null)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => subscribeRobotAlerts(setAlerts), [])
  // A queue change is exactly when a card rang out, so the clock is re-read with it.
  useEffect(
    () =>
      subscribeSosAlerts(next => {
        setQueue(next)
        setNow(Date.now())
      }),
    [],
  )
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), MISSED_TICK_MS)

    return () => clearInterval(timer)
  }, [])

  return useMemo(() => selectMissedSosAlerts(alerts, queue, now, covers), [alerts, queue, now, covers])
}
